import React, { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  X, UploadCloud, Loader2, Check, AlertTriangle, Plus, Sparkles, FileSpreadsheet, Leaf,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Card, Input, Select, Badge } from "./ui/Primitives";
import { formatEUR as fmtEur } from "../utils/format";


const ACTION_BUCKETS = {
  matched: { tone: "ok", label_key: "imports.bucket_matched" },
  unmatched: { tone: "warn", label_key: "imports.bucket_unmatched" },
  new: { tone: "default", label_key: "imports.bucket_new" },
};

/**
 * Smart Excel/CSV import modal.
 * @param {string} kind  "kitchen" | "bar"
 * @param {function} onClose
 * @param {function} onDone — called after a successful confirm
 */
export default function SmartImportModal({ kind = "kitchen", onClose, onDone }) {
  const { t } = useTranslation();
  const [step, setStep] = useState("upload"); // upload | review | done
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const fileRef = useRef();

  const uploadEndpoint = kind === "bar" ? "/cocktails/import" : "/ingredients/import";
  const confirmEndpoint = kind === "bar" ? "/imports/cocktails/confirm" : "/imports/ingredients/confirm";

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(uploadEndpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Convert items to editable rows
      setRows(data.items.map((it) => ({
        ...it,
        edited: {
          action: it.action === "matched" ? "update" : (it.action === "unmatched" ? "create" : "create"),
          ingredient_id: it.match_id || it.candidate_id || null,
          name: it.suggested?.name || it.source_name,
          category: it.suggested?.category || "Sonstiges",
          unit: it.suggested?.unit || "Stueck",
          quantity: it.suggested?.quantity || 1,
          purchase_price: it.suggested?.purchase_price || 0,
          allergens: [...(it.detected_allergens || [])],   // Phase 29 F6: prefilled from auto-detect
          is_seasonal: !!it.seasonal_detected,             // Phase 29 F6
        },
      })));
      setStep("review");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const updateRow = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => i === idx ? { ...r, edited: { ...r.edited, ...patch } } : r));
  };

  const toggleAllergen = (idx, key) => {
    setRows((rs) => rs.map((r, i) => {
      if (i !== idx) return r;
      const cur = new Set(r.edited.allergens || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...r, edited: { ...r.edited, allergens: [...cur].sort() } };
    }));
  };

  // Phase 29 F5 — inline validation. Confirm is disabled when any non-skip row
  // has an empty name, qty<=0 or no unit selected.
  const invalidRows = useMemo(() => {
    const out = [];
    rows.forEach((r, idx) => {
      if (r.edited.action === "skip") return;
      const name = (r.edited.name || "").trim();
      const qty = Number(r.edited.quantity || 0);
      const unit = r.edited.unit;
      if (!name || qty <= 0 || !unit) out.push(idx);
    });
    return out;
  }, [rows]);
  const canConfirm = invalidRows.length === 0 && rows.filter((r) => r.edited.action !== "skip").length > 0;

  const confirm = async () => {
    setBusy(true);
    try {
      const body = {
        source_filename: preview?.filename || "",
        kind,
        items: rows.map((r) => ({
          action: r.edited.action,
          ingredient_id: r.edited.action === "update" ? r.edited.ingredient_id : null,
          name: r.edited.name,
          category: r.edited.category,
          unit: r.edited.unit,
          quantity: Number(r.edited.quantity || 0),
          purchase_price: Number(r.edited.purchase_price || 0),
          allergens: r.edited.allergens || [],
          is_seasonal: !!r.edited.is_seasonal,
          notes: "",
        })),
      };
      const { data } = await api.post(confirmEndpoint, body);
      toast.success(t("imports.done_summary", { ...data.summary }));
      setStep("done");
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        className="bg-bg-void border-l border-white/10 w-full max-w-5xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="smart-import-modal"
      >
        <header className="sticky top-0 bg-bg-void border-b border-white/10 px-6 py-4 z-10 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-brand flex items-center gap-2">
              <Sparkles size={12} /> {t("imports.title")}
            </div>
            <h2 className="text-lg font-semibold">
              {kind === "bar" ? t("imports.title_bar") : t("imports.title_kitchen")}
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /></Button>
        </header>

        <div className="p-6 space-y-4">
          {step === "upload" && (
            <Card className="p-8 border-dashed border-2 border-white/15 flex flex-col items-center text-center gap-4" data-testid="import-dropzone">
              <UploadCloud size={36} className="text-brand" />
              <div>
                <h3 className="font-semibold mb-1">{t("imports.upload_title")}</h3>
                <p className="text-sm text-ink-secondary max-w-md">{t("imports.upload_hint")}</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.tsv,.xlsx,.xlsm"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="import-file-input"
              />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-pick-file">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
                {t("imports.pick_file")}
              </Button>
              <p className="text-xs text-ink-muted">{t("imports.supported_formats")}</p>
            </Card>
          )}

          {step === "review" && preview && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-4 border-ok/30" data-testid="kpi-matched">
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1.5"><Check size={11} className="text-ok" /> {t("imports.bucket_matched")}</div>
                  <div className="num text-2xl font-medium text-ok">{preview.matched}</div>
                </Card>
                <Card className="p-4 border-warn/30" data-testid="kpi-unmatched">
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1.5"><AlertTriangle size={11} className="text-warn" /> {t("imports.bucket_unmatched")}</div>
                  <div className="num text-2xl font-medium text-warn">{preview.unmatched}</div>
                </Card>
                <Card className="p-4" data-testid="kpi-new">
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1.5"><Plus size={11} className="text-brand" /> {t("imports.bucket_new")}</div>
                  <div className="num text-2xl font-medium text-brand">{preview.new}</div>
                </Card>
              </div>

              <Card className="overflow-hidden">
                <div className="overflow-x-auto max-h-[60vh]">
                  <table className="w-full text-sm" data-testid="import-rows-table">
                    <thead className="bg-white/[0.02] sticky top-0">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted">
                        <th className="px-3 py-2">{t("imports.col_source")}</th>
                        <th className="px-3 py-2">{t("imports.col_action")}</th>
                        <th className="px-3 py-2">{t("imports.col_name")}</th>
                        <th className="px-3 py-2 w-24">{t("imports.col_unit")}</th>
                        <th className="px-3 py-2 w-24 text-right">{t("imports.col_qty")}</th>
                        <th className="px-3 py-2 w-28 text-right">{t("imports.col_price")}</th>
                        <th className="px-3 py-2 w-32">{t("imports.col_category")}</th>
                        <th className="px-3 py-2 w-44">{t("imports.col_allergens", "Allergene")}</th>
                        <th className="px-3 py-2 w-16 text-center" title="Saisonal">
                          <Leaf size={11} className="inline text-ok" /> {t("imports.col_seasonal", "Saison")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => {
                        const bucket = ACTION_BUCKETS[r.action] || ACTION_BUCKETS.new;
                        const isInvalid = invalidRows.includes(idx);
                        const allergenSet = new Set(r.edited.allergens || []);
                        const detectedSet = new Set(r.detected_allergens || []);
                        return (
                          <tr
                            key={idx}
                            className={`border-t border-white/5 ${isInvalid ? "bg-err/5" : ""}`}
                            data-testid={`import-row-${idx}`}
                          >
                            <td className="px-3 py-2 align-top">
                              <div className="text-ink-primary text-xs">{r.source_name}</div>
                              <div className="text-ink-muted text-[10px] truncate max-w-[200px]">{r.raw_line || ""}</div>
                              <Badge tone={bucket.tone} className="mt-1 text-[9px]">
                                {t(bucket.label_key)} {r.score ? `· ${r.score}%` : ""}
                              </Badge>
                              {r.match_name && (
                                <div className="text-[10px] text-ok mt-1">→ {r.match_name}</div>
                              )}
                              {r.candidate_name && r.action === "unmatched" && (
                                <div className="text-[10px] text-warn mt-1">≈ {r.candidate_name}</div>
                              )}
                              {isInvalid && (
                                <div className="text-[10px] text-err mt-1" data-testid={`import-row-invalid-${idx}`}>
                                  {t("imports.row_invalid", "Bitte Name, Einheit & Menge prüfen")}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Select
                                value={r.edited.action}
                                onChange={(e) => updateRow(idx, { action: e.target.value })}
                                data-testid={`import-action-${idx}`}
                              >
                                {r.match_id && <option value="update">{t("imports.action_update")}</option>}
                                {r.candidate_id && <option value="update">{t("imports.action_update_to", { name: r.candidate_name })}</option>}
                                <option value="create">{t("imports.action_create")}</option>
                                <option value="skip">{t("imports.action_skip")}</option>
                              </Select>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Input
                                value={r.edited.name}
                                onChange={(e) => updateRow(idx, { name: e.target.value })}
                                data-testid={`import-name-${idx}`}
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Select value={r.edited.unit} onChange={(e) => updateRow(idx, { unit: e.target.value })}>
                                {(preview.units || []).map((u) => <option key={u} value={u}>{u}</option>)}
                              </Select>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Input
                                type="number"
                                step="0.01"
                                className="text-right num"
                                value={r.edited.quantity}
                                onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                                data-testid={`import-qty-${idx}`}
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Input
                                type="number"
                                step="0.01"
                                className="text-right num"
                                value={r.edited.purchase_price}
                                onChange={(e) => updateRow(idx, { purchase_price: e.target.value })}
                                data-testid={`import-price-${idx}`}
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Select value={r.edited.category} onChange={(e) => updateRow(idx, { category: e.target.value })}>
                                {(preview.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                              </Select>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex flex-wrap gap-1 max-w-[180px]" data-testid={`import-allergens-${idx}`}>
                                {(preview.allergens || []).map((a) => {
                                  const on = allergenSet.has(a);
                                  const auto = detectedSet.has(a);
                                  return (
                                    <button
                                      key={a}
                                      type="button"
                                      onClick={() => toggleAllergen(idx, a)}
                                      className={`text-[9px] px-1.5 py-0.5 rounded border transition ${
                                        on
                                          ? "bg-warn/20 border-warn/40 text-warn"
                                          : "bg-white/[0.03] border-white/10 text-ink-muted hover:bg-white/[0.05]"
                                      }`}
                                      title={auto ? t("imports.allergen_auto", "Automatisch erkannt") : ""}
                                      data-testid={`import-allergen-${idx}-${a}`}
                                    >
                                      {a}{auto && on ? "✱" : ""}
                                    </button>
                                  );
                                })}
                              </div>
                              {detectedSet.size > 0 && (
                                <div className="text-[9px] text-warn mt-1">
                                  {t("imports.auto_detected", "Auto: {{n}}", { n: r.detected_allergens.join(", ") })}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top text-center">
                              <input
                                type="checkbox"
                                checked={!!r.edited.is_seasonal}
                                onChange={(e) => updateRow(idx, { is_seasonal: e.target.checked })}
                                data-testid={`import-seasonal-${idx}`}
                                className="accent-ok"
                              />
                              {r.seasonal_detected && (
                                <div className="text-[9px] text-ok mt-1">
                                  {t("imports.seasonal_auto", "auto")}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              <div className="sticky bottom-0 bg-bg-void/95 backdrop-blur border-t border-white/10 py-3 -mx-6 px-6 flex items-center justify-between">
                <span className="text-xs text-ink-muted">
                  {invalidRows.length > 0 ? (
                    <span className="text-err" data-testid="import-invalid-count">
                      {t("imports.invalid_rows", "{{n}} Zeile(n) mit fehlenden Pflichtfeldern", { n: invalidRows.length })}
                    </span>
                  ) : (
                    t("imports.review_hint", { count: rows.filter((r) => r.edited.action !== "skip").length })
                  )}
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => { setStep("upload"); setPreview(null); setRows([]); }}>
                    {t("common.cancel")}
                  </Button>
                  <Button onClick={confirm} disabled={busy || !canConfirm} data-testid="import-confirm">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    {t("imports.confirm")}
                  </Button>
                </div>
              </div>
            </>
          )}

          {step === "done" && (
            <Card className="p-8 text-center space-y-3" data-testid="import-done">
              <Check size={36} className="text-ok mx-auto" />
              <h3 className="font-semibold">{t("imports.done_title")}</h3>
              <Button onClick={onClose}>{t("common.close", "Schließen")}</Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
